<script lang="ts">
  import { onMount, tick } from "svelte"
  import { xhr, toast, wait } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { CircleCheck, Plus, Pen, Trash2 } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    book: BookType
    mode?: string
    onadd: () => void
  }

  let { book = $bindable(), mode = "new", onadd }: Props = $props()

  book.book_readings = []

  let isLoading = $state(false)
  let currentReading: BookReadingType = $state(null)

  let newReadingEl = $state(null)
  let newTitleEl = $state(null)
  async function addBookReading(): Promise<void> {
    errors = {}
    title = ""
    formStatus = "new"
    await tick()
    scrollIntoView(newReadingEl)
    newTitleEl.focus()
  }

  onMount(() => {
    loadBookReadings()
  })

  async function loadBookReadings(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/books/${book.id}/book_readings.json`
      )

      console.log(resp.data)
      let book_readings = resp.data
      book.book_readings = book_readings.sort((a, b) => a.position - b.position)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the goal", "error")
    } finally {
      isLoading = false
    }
  }

  let errors = $state({})
  let title = $state("")
  let formStatus = $state("")
  async function createReading() {
    errors = {}

    try {
      const resp = await xhr.post(
        `/plans/${appStore.plan.id}/books/${book.id}/book_readings`,
        {
          book_reading: { title }
        }
      )

      const reading = resp.data
      book.book_readings = [...book.book_readings, reading]
      formStatus = ""
    } catch (e: any) {
      toast("There was an error saving the reading assignment", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    }
  }

  let editTitleEl = $state(null)
  async function editReading(reading: BookReadingType): Promise<void> {
    errors = {}
    formStatus = "edit"
    title = reading.title
    currentReading = reading
    await tick()
    editTitleEl.focus()
  }

  async function updateReading() {
    errors = {}
    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/books/${book.id}/book_readings/${currentReading.id}`,
        {
          book_reading: { title }
        }
      )

      let idx = book.book_readings.findIndex((b) => b.id === resp.data.id)

      book.book_readings[idx].title = resp.data.title
      formStatus = ""
    } catch (e: any) {
      toast("There was an error updating the reading assignment", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    }
  }

  async function deleteReading(reading: BookReadingType) {
    reading._status = "selected"
    book.book_readings = book.book_readings
    await wait(50)
    if (!confirm("Are you sure you want to delete the selected reading?")) {
      reading._status = ""
      book.book_readings = book.book_readings
      return
    }

    try {
      await xhr.delete(
        `/plans/${appStore.plan.id}/books/${book.id}/book_readings/${reading.id}`
      )

      let idx = book.book_readings.findIndex((b) => b.id === reading.id)
      book.book_readings.splice(idx, 1)
      book.book_readings = book.book_readings
      toast("Reading was successfully deleted", "success")
    } catch (e) {
      toast("There was an error deleting the reading", "error")
      console.error(e)
    } finally {
    }
  }

  const scrollIntoView = (node) => {
    node.scrollIntoView({
      behavior: "smooth"
    })
  }
</script>

<div class="mt-3 sm:mt-5 overflow-visible w-full">
  <div class="flex w-full justify-center items-center">
    <h3
      class="text-xl lg:text-3xl text-center leading-6 font-medium text-gray-900"
    >
      {#if mode === "new"}
        Add a Book
      {:else}
        Edit Book
      {/if}
    </h3>
  </div>
  <div class="grid grid-cols-12 gap-8 mt-4">
    <div class="col-span-5">
      <div class="font-semibold text-xl text-gray-800 text-center">
        {book.title}
      </div>

      <div class="mt-4 flex justify-center">
        <img
          src={book.image_url}
          alt={book.title}
          class="object-cover h-full"
        />
      </div>
    </div>
    <div class="col-span-7">
      <div>
        <h3 class="text-lg font-semibold text-gray-700">
          {#if mode === "new"}
            Create Your Reading Assignments
          {:else}
            Manage Reading Assignments
          {/if}
        </h3>
      </div>
      <div>
        {#if isLoading}
          <div class="text-gray-400">Loading...</div>
        {:else}
          <ul
            class="mt-2 border border-gray-300 rounded max-h-128 overflow-y-auto"
          >
            {#each book.book_readings as reading, i}
              <li
                class="flex justify-start items-center py-2 border-b border-gray-200 px-4"
                class:bg-red-200={reading._status === "selected"}
              >
                {#if formStatus === "edit" && currentReading.id === reading.id}
                  <div class="w-full">
                    <div
                      class="w-full flex items-center justify-between input-cont whitespace-nowrap"
                    >
                      <input
                        type="text"
                        class="input"
                        bind:value={title}
                        bind:this={editTitleEl}
                      />
                      <div class="ml-1">
                        <button
                          class="btn primary text-sm"
                          onclick={() => updateReading()}
                        >
                          Update
                        </button>
                        <button
                          class="btn secondary text-sm"
                          onclick={() => (formStatus = "")}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    <div class="text-red-50">
                      {errors.title || ""}
                    </div>
                  </div>
                {:else}
                  <div class="text-gray-400 text-2xl pr-4">{i + 1}</div>
                  <div class="text-base text-black px-4 grow">
                    {reading.title}
                  </div>
                  <button onclick={() => editReading(reading)}>
                    <Pen class="size-4" />
                  </button>
                  <button onclick={() => deleteReading(reading)} class="ml-4">
                    <Trash2 class="size-4" />
                  </button>
                {/if}
              </li>
            {:else}
              {#if formStatus !== "new"}
                <li class="text-gray-600 py-5 mx-4">
                  No reading assignments configured yet
                </li>
              {/if}
            {/each}
            {#if formStatus === "new"}
              <li class="py-5 mx-4" bind:this={newReadingEl}>
                <div
                  class="w-full flex items-center justify-between input-cont whitespace-nowrap"
                >
                  <input
                    type="text"
                    class="input"
                    bind:this={newTitleEl}
                    bind:value={title}
                    placeholder="Reading title"
                  />
                  <div class="ml-1">
                    <button class="btn primary" onclick={() => createReading()}>
                      Create
                    </button>
                    <button
                      class="btn secondary"
                      onclick={() => (formStatus = "")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                <div class="text-red-500">
                  {errors.title || ""}
                </div>
              </li>
            {/if}
          </ul>
        {/if}
      </div>
      <div class="mt-4">
        {#if formStatus === ""}
          <button
            onclick={addBookReading}
            class="flex items-center gap-1 hover:border-b border-blue-700 hover:text-blue-700"
          >
            <Plus class="size-4" />
            Add reading assignment row
          </button>
        {/if}
      </div>
      <div class="col-span-2 mt-6 flex items-center justify-center">
        <button
          class="w-full btn btn-rounded success btn-big"
          onclick={() => onadd()}
        >
          {#if mode === "new"}
            Save and Add Book
            <CircleCheck class="ml-2 size-6" />
          {:else}
            Finished Editing
          {/if}
        </button>
      </div>
    </div>
  </div>
</div>
